import {
    AfterViewChecked,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    Output,
    SimpleChanges,
    OnChanges,
    ViewChild,
} from '@angular/core'
import { Booking } from '@app/domain/Booking'
import { BookingCommunicationMessage } from '@app/domain/BookingCommunicationMessage'
import { MessageThread } from '@app/domain/MessageThread'
import { Venue } from '@app/domain/Venue'

@Component({
    selector: 'app-message-thread',
    templateUrl: './message-thread.component.html',
    styleUrls: ['./message-thread.component.sass'],
})
export class MessageThreadComponent implements AfterViewChecked, OnChanges {

    @Input() venue!: Venue
    @Input() thread: MessageThread | null = null
    @Input() booking: Booking | null = null
    @Input() messages: BookingCommunicationMessage[] = []
    @Input() loading = false
    @Input() sendingMessage = false
    @Input() hideHeader = false
    @Input() canSendMessages = true
    @Input() isRequestedBooking = false
    @Input() requestActionLoading = false
    @Output() messageSent = new EventEmitter<{ body: string, subject?: string }>()
    @Output() acceptRequestedBooking = new EventEmitter<void>()
    @Output() rejectRequestedBooking = new EventEmitter<void>()

    @ViewChild('messagesContainer') messagesContainer!: ElementRef
    @ViewChild('composeTextarea') composeTextarea!: ElementRef<HTMLTextAreaElement>

    newMessageBody = ''
    private shouldScrollToBottom = false

    ngOnChanges(changes: SimpleChanges) {
        if (changes['messages'] || changes['thread']) {
            this.shouldScrollToBottom = true
        }
    }

    ngAfterViewChecked() {
        if (this.shouldScrollToBottom) {
            this.scrollToBottom()
            this.shouldScrollToBottom = false
        }
    }

    shouldShowDateSeparator(index: number): boolean {
        if (index === 0) return true
        const currentDate = this.messages[index].dateCreated
        const previousDate = this.messages[index - 1].dateCreated
        return currentDate.toDateString() !== previousDate.toDateString()
    }

    shouldShowSenderLabel(index: number): boolean {
        if (index === 0) return true
        const currentSender = this.messages[index].senderType
        const previousSender = this.messages[index - 1].senderType
        if (currentSender !== previousSender) return true
        return this.shouldShowDateSeparator(index)
    }

    autoResizeTextarea(event: Event) {
        const textarea = event.target as HTMLTextAreaElement
        textarea.style.height = 'auto'
        textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px'
    }

    sendMessage() {
        const body = this.newMessageBody.trim()
        if (!body || this.sendingMessage || !this.canSendMessages) {
            return
        }
        const subject = this.threadSubject() || undefined
        this.messageSent.emit({ body, subject })
        this.newMessageBody = ''
        this.resetTextareaHeight()
        this.shouldScrollToBottom = true
    }

    onKeyDown(event: KeyboardEvent) {
        if (!this.canSendMessages) {
            return
        }
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault()
            this.sendMessage()
        }
    }

    acceptBookingRequest() {
        this.acceptRequestedBooking.emit()
    }

    rejectBookingRequest() {
        this.rejectRequestedBooking.emit()
    }

    trackByMessageId(_index: number, message: BookingCommunicationMessage): string {
        return message.id
    }

    private scrollToBottom() {
        try {
            const container = this.messagesContainer?.nativeElement
            if (container) {
                container.scrollTop = container.scrollHeight
            }
        } catch (_) {
            // Scroll may fail if container not yet rendered
        }
    }

    private resetTextareaHeight() {
        const textarea = this.composeTextarea?.nativeElement
        if (textarea) {
            textarea.style.height = 'auto'
        }
    }

    private threadSubject(): string | null {
        if (!this.booking) {
            return null
        }
        const mostRecentMessageWithSubject = [...this.messages]
            .reverse()
            .find(message => message.subject)
        if (mostRecentMessageWithSubject) {
            return mostRecentMessageWithSubject.subject
        }
        const dateString = this.booking.start.toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        })
         return `Your Booking at ${this.venue.displayName} on ${dateString}`
     }
}
